/**
 * CiteScoreBadge - V3 source trust indicator.
 *
 * Two rendering modes based on available data:
 *
 * 1. Journal sources (both score + impact_factor present):
 *    Numeric box with IF value + tier label (Tier 1/2/3) + "Impact score" footnote.
 *    Figma: node 7520:32170
 *
 * 2. Non-journal sources (score present, no impact_factor):
 *    Falls back to ConfidenceBadge — a simple coloured pill
 *    ("High confidence" / "Moderate confidence" / "Low confidence").
 *    Figma: node 7556:8122
 *
 * Label rationale: docs/design-docs/source-card/impact-score-labels.md
 */

import { useTranslations } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import { getCiteScoreTier } from './authorityTiers';
import { ConfidenceBadge } from './ConfidenceBadge';

interface CiteScoreBadgeProps {
  /** Authoritativeness score (0–100) used to determine tier/colour */
  score: number | null | undefined;
  /** The impact score value to display (typically impact_factor) */
  citeScore: number | null | undefined;
  className?: string;
}

export function CiteScoreBadge({ score, citeScore, className }: CiteScoreBadgeProps) {
  const { t } = useTranslations();
  const tier = getCiteScoreTier(score);

  if (!tier) return null;

  if (citeScore === undefined || citeScore === null) {
    return <ConfidenceBadge score={score} className={className} />;
  }

  return (
    <div
      data-testid="source-reliability-classification"
      className={cn('flex items-center gap-2', className)}
    >
      <div
        className={cn(
          'flex h-[30px] min-w-[30px] items-center justify-center rounded-md px-1.5',
          tier.scoreBgClass,
          tier.scoreTextClass
        )}
      >
        <span className="text-xs font-medium leading-none">{citeScore.toFixed(1)}</span>
      </div>
      <div className="flex flex-col items-start">
        <span className="text-xs font-medium leading-none text-text-primary">
          {t(tier.labelKey)}
        </span>
        <span className="mt-1 text-[10px] leading-none text-text-secondary">
          {t('chat.impactScore')}
        </span>
      </div>
    </div>
  );
}
